/**
 * adoptionAnalytics.js
 *
 * Pure, framework-free analytics for the adoption SPA. Deterministic and
 * side-effect free so it can be unit tested and reused by the UI or a script.
 *
 * Event model (one user prompt occurrence):
 *   { user: string(email, lowercased), ts: string(ISO-8601), text: string,
 *     conversationId?: string, source?: string }
 *
 * Usage = ACTIVE DAYS per week (distinct days a user cleared the per-day query
 * threshold). Loyalty = week-over-week new vs returning. Histograms = multi-label
 * over scientific area and use case.
 */

// ---------------------------------------------------------------------------
// Taxonomy (kept in sync with the backend /api/usage/categorize contract)
// ---------------------------------------------------------------------------
export const SCIENTIFIC_AREAS = [
  'Oncology',
  'Neuroscience & CNS',
  'Immunology & Inflammation',
  'Fibrosis & Respiratory',
  'Metabolic & Endocrine',
  'Cardiovascular',
  'Infectious Disease',
  'Epigenetics & Chromatin',
  'DNA Damage Response & Genome Stability',
  'Cell Signaling & Transcription',
  'Proteostasis & Protein Degradation',
  'Mitochondria & Cellular Metabolism',
  'Stem Cell & Developmental Biology',
  'Rare & Genetic Disease',
  'Methodology/Tooling',
  'Unspecified/General',
];

export const USE_CASES = [
  'Protein-Protein Interactions',
  'Target Identification & Prioritization',
  'Drug & Small-Molecule Discovery/Repurposing',
  'Mechanism of Action & Pathway Analysis',
  'Disease Association & Biomarkers',
  'Gene/Protein Function & Annotation',
  'Literature & Evidence Synthesis',
  'Compound/Tool-Compound Lookup',
  'Experimental Design & Data Interpretation',
  'Summarization/Formatting/Follow-up',
  'Other',
];

export const DEFAULT_COMPANY_ALIASES = {
  'emdserono.com': 'EMD Serono / Merck KGaA',
  'merckgroup.com': 'EMD Serono / Merck KGaA',
};

export const DEFAULT_EXCLUDED_DOMAINS = ['plexresearch.com', 'gmail.com'];
export const DEFAULT_EXCLUDED_USERS = ['system', 'anonymous'];

// Key delimiter for composite map keys. Use a control char unlikely to appear
// in emails or week strings so keys never collide.
const SEP = '';

// ---------------------------------------------------------------------------
// Date helpers (UTC, ISO weeks starting Monday)
// ---------------------------------------------------------------------------
export function dayKey(d) {
  const date = d instanceof Date ? d : new Date(d);
  return date.toISOString().slice(0, 10);
}

export function isoWeekStart(d) {
  const date = d instanceof Date ? new Date(d.getTime()) : new Date(d);
  const dow = (date.getUTCDay() + 6) % 7; // Monday = 0
  date.setUTCDate(date.getUTCDate() - dow);
  return date.toISOString().slice(0, 10);
}

export function buildWeeks(minDay, maxDay) {
  const weeks = [];
  let cur = new Date(isoWeekStart(minDay) + 'T00:00:00Z');
  const end = new Date(isoWeekStart(maxDay) + 'T00:00:00Z');
  while (cur.getTime() <= end.getTime()) {
    weeks.push(cur.toISOString().slice(0, 10));
    cur.setUTCDate(cur.getUTCDate() + 7);
  }
  return weeks;
}

// ---------------------------------------------------------------------------
// Normalization
// ---------------------------------------------------------------------------
export function domainOf(email) {
  const e = String(email || '').toLowerCase();
  const at = e.indexOf('@');
  return at >= 0 ? e.slice(at + 1) : e;
}

export function companyOf(email, aliases = DEFAULT_COMPANY_ALIASES) {
  return aliases[domainOf(email)] || domainOf(email);
}

export function extractPromptText(messages, initialPrompt) {
  if (initialPrompt && String(initialPrompt).trim()) return String(initialPrompt).trim();
  let arr = messages;
  if (typeof messages === 'string') {
    try { arr = JSON.parse(messages); } catch (e) { return String(messages).trim() || null; }
  }
  if (!Array.isArray(arr)) return null;
  for (const m of arr) {
    if (m && m.role === 'user') {
      const c = m.content;
      if (Array.isArray(c)) {
        for (const part of c) {
          if (part && typeof part.text === 'string' && part.text.trim()) return part.text.trim();
        }
      } else if (typeof c === 'string' && c.trim()) {
        return c.trim();
      }
    }
  }
  return null;
}

export function normalizeEvents(raw, opts = {}) {
  const excludedDomains = new Set((opts.excludedDomains || DEFAULT_EXCLUDED_DOMAINS).map((d) => d.toLowerCase()));
  const excludedUsers = new Set((opts.excludedUsers || DEFAULT_EXCLUDED_USERS).map((u) => u.toLowerCase()));

  let rows = raw;
  if (raw && !Array.isArray(raw)) rows = raw.events || raw.prompts || raw.rows || raw.prompts_data || [];
  if (!Array.isArray(rows)) return [];

  const out = [];
  for (const r of rows) {
    if (!r) continue;
    const user = String(r.user || r.email || '').trim().toLowerCase();
    if (!user || excludedUsers.has(user)) continue;
    if (excludedDomains.has(domainOf(user))) continue;

    const ts = r.ts || r.timestamp || r.time || r['@timestamp'];
    if (!ts) continue;
    const parsed = new Date(ts);
    if (isNaN(parsed.getTime())) continue;

    const text = typeof r.text === 'string' && r.text.trim()
      ? r.text.trim()
      : extractPromptText(r.messages, r.initial_prompt);
    if (!text) continue;

    const status = r.status ?? r.st ?? null;
    const latencyMs = r.latencyMs ?? r.ms ?? null;
    out.push({
      user,
      ts: parsed.toISOString(),
      text,
      conversationId: r.conversationId || r.conversation_id || null,
      source: r.source || null,
      status: typeof status === 'number' ? status : null,
      latencyMs: typeof latencyMs === 'number' ? latencyMs : null,
    });
  }
  return out;
}

export function distinctPrompts(events, aliases = DEFAULT_COMPANY_ALIASES) {
  const seen = new Set();
  const out = [];
  for (const e of events) {
    const company = companyOf(e.user, aliases);
    const key = company + SEP + e.text;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ company, user: e.user, text: e.text });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Usage + loyalty aggregation
// ---------------------------------------------------------------------------

/**
 * Aggregate normalized events into weekly usage + loyalty structures.
 *
 * opts.minQueriesPerDay (default 0): a day counts as "active" only if it has
 * MORE THAN this many interactions. 0 => any day with >=1 (original behavior).
 * The week window (x-axis) is always derived from all events so the timeline is
 * stable as the threshold changes.
 */
export function aggregate(events, opts = {}) {
  const aliases = opts.aliases || DEFAULT_COMPANY_ALIASES;
  const minQ = opts.minQueriesPerDay || 0;

  if (!events || events.length === 0) {
    return {
      weeks: [], users: [], companies: [], userCompany: {},
      userMatrix: {}, compUserDays: {}, compUsers: {}, totalUserDays: [],
      activeUsers: [], newUsers: [], returningUsers: [], retainedPrev: [],
      perUser: {}, userEvents: {}, compEvents: {}, dateRange: [null, null],
    };
  }

  const days = events.map((e) => dayKey(e.ts));
  const minDay = days.reduce((a, b) => (a < b ? a : b));
  const maxDay = days.reduce((a, b) => (a > b ? a : b));
  const weeks = buildWeeks(minDay, maxDay);
  const wIndex = new Map(weeks.map((w, i) => [w, i]));
  const N = weeks.length;

  const userWeekDays = new Map();   // `${user}${SEP}${week}` -> Set(dayKey)
  const compWeekDays = new Map();   // `${company}${SEP}${week}` -> Set(`${user}${SEP}${day}`)
  const compWeekUsers = new Map();  // `${company}${SEP}${week}` -> Set(user)
  const userEvents = {};
  const compEvents = {};
  const userCompany = {};
  const usersSet = new Set();
  const companiesSet = new Set();

  // Pass 1: totals + per-(user,day) interaction counts.
  const dayCount = new Map(); // `${user}${SEP}${day}` -> count
  for (const e of events) {
    const company = companyOf(e.user, aliases);
    const week = isoWeekStart(e.ts);
    if (!wIndex.has(week)) continue;
    usersSet.add(e.user);
    companiesSet.add(company);
    userCompany[e.user] = company;
    userEvents[e.user] = (userEvents[e.user] || 0) + 1;
    compEvents[company] = (compEvents[company] || 0) + 1;
    const dk = e.user + SEP + dayKey(e.ts);
    dayCount.set(dk, (dayCount.get(dk) || 0) + 1);
  }

  // Pass 2: only days clearing the threshold count toward active days.
  for (const [dk, count] of dayCount) {
    if (count <= minQ) continue;
    const sep = dk.indexOf(SEP);
    const user = dk.slice(0, sep);
    const day = dk.slice(sep + 1);
    const week = isoWeekStart(day);
    const company = userCompany[user];

    const uk = user + SEP + week;
    if (!userWeekDays.has(uk)) userWeekDays.set(uk, new Set());
    userWeekDays.get(uk).add(day);

    const ck = company + SEP + week;
    if (!compWeekDays.has(ck)) compWeekDays.set(ck, new Set());
    compWeekDays.get(ck).add(user + SEP + day);
    if (!compWeekUsers.has(ck)) compWeekUsers.set(ck, new Set());
    compWeekUsers.get(ck).add(user);
  }

  const users = [...usersSet].sort();
  const companies = [...companiesSet].sort();

  const userMatrix = {};
  for (const u of users) {
    userMatrix[u] = weeks.map((w) => {
      const s = userWeekDays.get(u + SEP + w);
      return s ? s.size : 0;
    });
  }

  const compUserDays = {};
  const compUsers = {};
  for (const c of companies) {
    compUserDays[c] = weeks.map((w) => {
      const s = compWeekDays.get(c + SEP + w);
      return s ? s.size : 0;
    });
    compUsers[c] = weeks.map((w) => {
      const s = compWeekUsers.get(c + SEP + w);
      return s ? s.size : 0;
    });
  }

  const totalUserDays = weeks.map((_, i) => companies.reduce((s, c) => s + compUserDays[c][i], 0));

  const firstWeek = {};
  for (const u of users) firstWeek[u] = userMatrix[u].findIndex((v) => v > 0);

  const newUsers = new Array(N).fill(0);
  const returningUsers = new Array(N).fill(0);
  const activeUsers = new Array(N).fill(0);
  const retainedPrev = new Array(N).fill(0);
  for (let i = 0; i < N; i++) {
    for (const u of users) {
      if (userMatrix[u][i] > 0) {
        activeUsers[i] += 1;
        if (firstWeek[u] === i) newUsers[i] += 1;
        else returningUsers[i] += 1;
        if (i > 0 && userMatrix[u][i - 1] > 0) retainedPrev[i] += 1;
      }
    }
  }

  const perUser = {};
  for (const u of users) {
    const m = userMatrix[u];
    const fi = firstWeek[u];
    let li = -1;
    for (let i = N - 1; i >= 0; i--) { if (m[i] > 0) { li = i; break; } }
    perUser[u] = {
      company: userCompany[u],
      events: userEvents[u] || 0,
      activeWeeks: m.filter((v) => v > 0).length,
      activeDays: m.reduce((a, b) => a + b, 0),
      first: fi >= 0 ? weeks[fi] : null,
      last: li >= 0 ? weeks[li] : null,
    };
  }

  return {
    weeks, users, companies, userCompany,
    userMatrix, compUserDays, compUsers, totalUserDays,
    activeUsers, newUsers, returningUsers, retainedPrev,
    perUser, userEvents, compEvents,
    dateRange: [minDay, maxDay],
  };
}

export function wowRetention(agg) {
  const { weeks, activeUsers, retainedPrev } = agg;
  return weeks.map((_, i) => {
    if (i === 0 || activeUsers[i - 1] === 0) return null;
    return retainedPrev[i] / activeUsers[i - 1];
  });
}

export function avgWowRetention(agg) {
  const rates = wowRetention(agg).filter((r) => r != null);
  if (!rates.length) return 0;
  return rates.reduce((a, b) => a + b, 0) / rates.length;
}

// ---------------------------------------------------------------------------
// Classification histograms (multi-label)
// ---------------------------------------------------------------------------
export function histograms(classifiedPrompts) {
  const aggArea = {};
  const aggUse = {};
  const compArea = {};
  const compUse = {};
  const compTotal = {};
  const bump = (obj, key) => { obj[key] = (obj[key] || 0) + 1; };

  for (const p of classifiedPrompts) {
    const c = p.company;
    compTotal[c] = (compTotal[c] || 0) + 1;
    if (!compArea[c]) compArea[c] = {};
    if (!compUse[c]) compUse[c] = {};
    const areas = (p.areas && p.areas.length) ? p.areas : ['Unspecified/General'];
    const uses = (p.uses && p.uses.length) ? p.uses : ['Other'];
    for (const a of areas) { bump(aggArea, a); bump(compArea[c], a); }
    for (const u of uses) { bump(aggUse, u); bump(compUse[c], u); }
  }
  return { aggArea, aggUse, compArea, compUse, compTotal };
}

// ---------------------------------------------------------------------------
// Executive "Platform Adoption" report (mirrors the leadership deck view)
// ---------------------------------------------------------------------------

/** A "champion" is a user with at least this many research interactions. */
export const CHAMPION_MIN_QUERIES = 10;
/** A "named" organization has >= this many interactions OR >= 2 active users. */
export const NAMED_MIN_INTERACTIONS = 25;

const EMPTY_REPORT = {
  interactions: 0, scientists: 0,
  organizations: { total: 0, named: 0, emerging: 0 },
  championsCount: 0,
  daysActive: { active: 0, total: 0, pct: 0 },
  avgQueriesPerDay: 0, growthPct: null,
  weeks: [], weekInteractions: [], cumScientists: [], cumChampions: [],
  byOrg: [], byUser: [], dateRange: [null, null],
};

/**
 * Compute the executive adoption summary from normalized events.
 * "Interactions" = every logged user prompt event (not de-duplicated).
 */
export function adoptionReport(events, opts = {}) {
  const aliases = opts.aliases || DEFAULT_COMPANY_ALIASES;
  const championMin = opts.championMin ?? CHAMPION_MIN_QUERIES;
  const namedMin = opts.namedMinInteractions ?? NAMED_MIN_INTERACTIONS;
  if (!events || !events.length) return { ...EMPTY_REPORT };

  const days = events.map((e) => dayKey(e.ts));
  const minDay = days.reduce((a, b) => (a < b ? a : b));
  const maxDay = days.reduce((a, b) => (a > b ? a : b));
  const weeks = buildWeeks(minDay, maxDay);
  const N = weeks.length;
  const wIndex = new Map(weeks.map((w, i) => [w, i]));

  const userCount = {}, userFirstW = {}, compCount = {}, compUsers = {};
  const weekInteractions = new Array(N).fill(0);
  const activeDays = new Set();

  for (const e of events) {
    const idx = wIndex.get(isoWeekStart(e.ts));
    if (idx == null) continue;
    weekInteractions[idx] += 1;
    userCount[e.user] = (userCount[e.user] || 0) + 1;
    if (userFirstW[e.user] == null || idx < userFirstW[e.user]) userFirstW[e.user] = idx;
    const c = companyOf(e.user, aliases);
    compCount[c] = (compCount[c] || 0) + 1;
    (compUsers[c] || (compUsers[c] = new Set())).add(e.user);
    activeDays.add(dayKey(e.ts));
  }

  const users = Object.keys(userCount);
  const champions = new Set(users.filter((u) => userCount[u] >= championMin));
  const cumScientists = new Array(N).fill(0);
  const cumChampions = new Array(N).fill(0);
  for (let i = 0; i < N; i++) {
    cumScientists[i] = users.filter((u) => userFirstW[u] <= i).length;
    cumChampions[i] = [...champions].filter((u) => userFirstW[u] <= i).length;
  }

  const totalDays = Math.round((Date.parse(maxDay) - Date.parse(minDay)) / 86400000) + 1;
  const interactions = events.length;

  const mid = (Date.parse(minDay) + Date.parse(maxDay)) / 2;
  let firstHalf = 0, secondHalf = 0;
  for (const e of events) (Date.parse(dayKey(e.ts)) <= mid ? firstHalf++ : secondHalf++);
  const growthPct = firstHalf > 0 ? Math.round((100 * (secondHalf - firstHalf)) / firstHalf) : null;

  const companies = Object.keys(compCount);
  const named = companies.filter((c) => compCount[c] >= namedMin || compUsers[c].size >= 2);
  const byOrg = companies
    .map((c) => ({ name: c, interactions: compCount[c], users: compUsers[c].size }))
    .sort((a, b) => b.interactions - a.interactions);
  const byUser = users
    .map((u) => ({ name: u, interactions: userCount[u] }))
    .sort((a, b) => b.interactions - a.interactions);

  return {
    interactions,
    scientists: users.length,
    organizations: { total: companies.length, named: named.length, emerging: companies.length - named.length },
    championsCount: champions.size,
    daysActive: { active: activeDays.size, total: totalDays, pct: totalDays ? Math.round((100 * activeDays.size) / totalDays) : 0 },
    avgQueriesPerDay: activeDays.size ? +(interactions / activeDays.size).toFixed(1) : 0,
    growthPct,
    weeks, weekInteractions, cumScientists, cumChampions,
    byOrg, byUser,
    dateRange: [minDay, maxDay],
  };
}

/** Collapse a sorted [{name,interactions}] list into top-N + an "Other" bucket. */
export function topNWithOther(rows, n, otherLabel = 'Other') {
  if (rows.length <= n) return rows.map((r) => ({ ...r }));
  const top = rows.slice(0, n).map((r) => ({ ...r }));
  const rest = rows.slice(n);
  top.push({ name: otherLabel, interactions: rest.reduce((s, r) => s + r.interactions, 0), other: rest.length });
  return top;
}

/** Validate/sanitize a label list against the allowed taxonomy. */
export function sanitizeLabels(labels, allowed, fallback) {
  const set = new Set(allowed);
  const out = (labels || []).filter((l) => set.has(l));
  return out.length ? out : [fallback];
}

// ===========================================================================
// Standard product metrics (engagement, retention, activation, concentration,
// account health, quality/ops). All pure; all derive from normalized events.
// ===========================================================================

/** Linear-interpolated percentile of a numeric array (p in 0..100). */
export function percentile(values, p) {
  const a = values.filter((v) => typeof v === 'number' && !isNaN(v)).sort((x, y) => x - y);
  if (!a.length) return null;
  const idx = (p / 100) * (a.length - 1);
  const lo = Math.floor(idx), hi = Math.ceil(idx);
  if (lo === hi) return a[lo];
  return a[lo] + (a[hi] - a[lo]) * (idx - lo);
}

function weekIndexer(events) {
  const days = events.map((e) => dayKey(e.ts));
  const minDay = days.reduce((a, b) => (a < b ? a : b));
  const maxDay = days.reduce((a, b) => (a > b ? a : b));
  const weeks = buildWeeks(minDay, maxDay);
  const wIndex = new Map(weeks.map((w, i) => [w, i]));
  return { weeks, wIndex, minDay, maxDay };
}

/** Per-user set of active week indices + first-active week index. */
function userWeekSets(events, wIndex) {
  const active = new Map(); // user -> Set(weekIdx)
  const first = new Map();
  for (const e of events) {
    const i = wIndex.get(isoWeekStart(e.ts));
    if (i == null) continue;
    if (!active.has(e.user)) active.set(e.user, new Set());
    active.get(e.user).add(i);
    if (!first.has(e.user) || i < first.get(e.user)) first.set(e.user, i);
  }
  return { active, first };
}

/**
 * Stickiness: weekly active users (WAU) vs trailing-4-week active users (a
 * monthly-like MAU), and the mean WAU/MAU ratio. Higher = more habitual.
 */
export function stickiness(events) {
  if (!events.length) return { weeks: [], wau: [], mau: [], avgWau: 0, avgMau: 0, stickiness: 0 };
  const { weeks, wIndex } = weekIndexer(events);
  const byWeek = weeks.map(() => new Set());
  for (const e of events) {
    const i = wIndex.get(isoWeekStart(e.ts));
    if (i != null) byWeek[i].add(e.user);
  }
  const wau = byWeek.map((s) => s.size);
  const mau = weeks.map((_, i) => {
    const u = new Set();
    for (let k = Math.max(0, i - 3); k <= i; k++) byWeek[k].forEach((x) => u.add(x));
    return u.size;
  });
  const ratios = weeks.map((_, i) => (mau[i] > 0 ? wau[i] / mau[i] : null)).filter((r) => r != null);
  const mean = (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : 0);
  return { weeks, wau, mau, avgWau: +mean(wau).toFixed(1), avgMau: +mean(mau).toFixed(1), stickiness: +mean(ratios).toFixed(3) };
}

/** Split each user's events into sessions separated by > gapMinutes of inactivity. */
export function sessionize(events, gapMinutes = 30) {
  const gap = gapMinutes * 60000;
  const byUser = new Map();
  for (const e of events) {
    if (!byUser.has(e.user)) byUser.set(e.user, []);
    byUser.get(e.user).push(Date.parse(e.ts));
  }
  const sessions = [];
  for (const [user, ts] of byUser) {
    ts.sort((a, b) => a - b);
    let start = ts[0], prev = ts[0], count = 1;
    for (let i = 1; i < ts.length; i++) {
      if (ts[i] - prev > gap) {
        sessions.push({ user, start, end: prev, count, durationMin: (prev - start) / 60000 });
        start = ts[i]; count = 1;
      } else count += 1;
      prev = ts[i];
    }
    sessions.push({ user, start, end: prev, count, durationMin: (prev - start) / 60000 });
  }
  return sessions;
}

/** Session-depth summary from sessionize(). */
export function sessionMetrics(events, gapMinutes = 30) {
  const sessions = sessionize(events, gapMinutes);
  if (!sessions.length) return { sessions: 0, medianQueriesPerSession: 0, multiQueryPct: 0, medianDurationMin: 0, avgSessionsPerUser: 0 };
  const counts = sessions.map((s) => s.count);
  const users = new Set(sessions.map((s) => s.user)).size;
  const multi = sessions.filter((s) => s.count > 1).length;
  return {
    sessions: sessions.length,
    medianQueriesPerSession: percentile(counts, 50),
    multiQueryPct: Math.round((100 * multi) / sessions.length),
    medianDurationMin: +percentile(sessions.map((s) => s.durationMin), 50).toFixed(1),
    avgSessionsPerUser: +(sessions.length / users).toFixed(2),
  };
}

/** Median & mean interactions per active user per week. */
export function intensity(events) {
  if (!events.length) return { medianPerUserWeek: 0, meanPerUserWeek: 0 };
  const { wIndex } = weekIndexer(events);
  const counts = new Map(); // `${user}|${week}` -> n
  for (const e of events) {
    const i = wIndex.get(isoWeekStart(e.ts));
    if (i == null) continue;
    const k = e.user + '|' + i;
    counts.set(k, (counts.get(k) || 0) + 1);
  }
  const vals = [...counts.values()];
  return {
    medianPerUserWeek: percentile(vals, 50),
    meanPerUserWeek: +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2),
  };
}

/**
 * Weekly cohort retention triangle. Cohort = user's first-active week. Cell
 * [c][k] = % of cohort c active k weeks later.
 */
export function cohortRetention(events) {
  if (!events.length) return { weeks: [], cohorts: [] };
  const { weeks, wIndex } = weekIndexer(events);
  const { active, first } = userWeekSets(events, wIndex);
  const N = weeks.length;
  const members = weeks.map(() => []);
  for (const [u, f] of first) members[f].push(u);
  const cohorts = [];
  for (let c = 0; c < N; c++) {
    if (!members[c].length) continue;
    const size = members[c].length;
    const ret = [];
    for (let k = 0; c + k < N; k++) {
      const cnt = members[c].filter((u) => active.get(u).has(c + k)).length;
      ret.push({ k, count: cnt, pct: Math.round((100 * cnt) / size) });
    }
    cohorts.push({ weekIndex: c, week: weeks[c], size, ret });
  }
  return { weeks, cohorts };
}

/**
 * Growth accounting of active users per week: new / retained / resurrected /
 * churned, plus the overall Quick Ratio = (new+resurrected) / churned.
 */
export function growthAccounting(events) {
  if (!events.length) return { weeks: [], newU: [], retained: [], resurrected: [], churned: [], quickRatio: null };
  const { weeks, wIndex } = weekIndexer(events);
  const { active, first } = userWeekSets(events, wIndex);
  const N = weeks.length;
  const activeAt = (u, i) => active.get(u).has(i);
  const allUsers = [...active.keys()];
  const newU = new Array(N).fill(0), retained = new Array(N).fill(0), resurrected = new Array(N).fill(0), churned = new Array(N).fill(0);
  for (let i = 0; i < N; i++) {
    for (const u of allUsers) {
      const now = activeAt(u, i), prev = i > 0 && activeAt(u, i - 1);
      if (now) {
        if (first.get(u) === i) newU[i] += 1;
        else if (prev) retained[i] += 1;
        else resurrected[i] += 1;
      } else if (prev) {
        churned[i] += 1; // count as churned in the week they went silent
      }
    }
  }
  const gained = newU.reduce((a, b) => a + b, 0) + resurrected.reduce((a, b) => a + b, 0);
  const lost = churned.reduce((a, b) => a + b, 0);
  return { weeks, newU, retained, resurrected, churned, quickRatio: lost > 0 ? +(gained / lost).toFixed(2) : null };
}

/**
 * Activation: share of users reaching `valueN` interactions (= "champion"),
 * median days from first interaction to that point, and week-1 retention of
 * new-user cohorts.
 */
export function activation(events, opts = {}) {
  const valueN = opts.valueN ?? CHAMPION_MIN_QUERIES;
  if (!events.length) return { activationRate: 0, medianDaysToValue: null, activatedUsers: 0, totalUsers: 0, w1Retention: null, valueN };
  const byUser = new Map();
  for (const e of events) {
    if (!byUser.has(e.user)) byUser.set(e.user, []);
    byUser.get(e.user).push(Date.parse(e.ts));
  }
  let activated = 0;
  const daysToValue = [];
  for (const ts of byUser.values()) {
    ts.sort((a, b) => a - b);
    if (ts.length >= valueN) {
      activated += 1;
      daysToValue.push((ts[valueN - 1] - ts[0]) / 86400000);
    }
  }
  const totalUsers = byUser.size;

  // week-1 retention: of users whose first week is not the last week, share
  // active again the very next week.
  const { weeks, wIndex } = weekIndexer(events);
  const { active, first } = userWeekSets(events, wIndex);
  const N = weeks.length;
  let eligible = 0, retainedW1 = 0;
  for (const [u, f] of first) {
    if (f < N - 1) { eligible += 1; if (active.get(u).has(f + 1)) retainedW1 += 1; }
  }
  return {
    activationRate: Math.round((100 * activated) / totalUsers),
    medianDaysToValue: daysToValue.length ? +percentile(daysToValue, 50).toFixed(1) : null,
    activatedUsers: activated,
    totalUsers,
    w1Retention: eligible ? Math.round((100 * retainedW1) / eligible) : null,
    valueN,
  };
}

/**
 * Concentration / dependency risk: per-week top-account share and HHI
 * (0..1; higher = more concentrated), plus the overall share of interactions
 * from the top decile of users.
 */
export function concentration(events, opts = {}) {
  const aliases = opts.aliases || DEFAULT_COMPANY_ALIASES;
  if (!events.length) return { weeks: [], top1: [], hhi: [], topDecileUserShare: 0, topAccount: null, topAccountSharePct: 0 };
  const { weeks, wIndex } = weekIndexer(events);
  const compByWeek = weeks.map(() => new Map());
  const compTotal = new Map();
  const userTotal = new Map();
  for (const e of events) {
    const i = wIndex.get(isoWeekStart(e.ts));
    if (i == null) continue;
    const c = companyOf(e.user, aliases);
    compByWeek[i].set(c, (compByWeek[i].get(c) || 0) + 1);
    compTotal.set(c, (compTotal.get(c) || 0) + 1);
    userTotal.set(e.user, (userTotal.get(e.user) || 0) + 1);
  }
  const top1 = [], hhi = [];
  for (let i = 0; i < weeks.length; i++) {
    const counts = [...compByWeek[i].values()];
    const tot = counts.reduce((a, b) => a + b, 0);
    if (!tot) { top1.push(0); hhi.push(0); continue; }
    top1.push(Math.round((100 * Math.max(...counts)) / tot));
    hhi.push(+counts.reduce((s, c) => s + (c / tot) ** 2, 0).toFixed(3));
  }
  const totalAll = [...userTotal.values()].reduce((a, b) => a + b, 0);
  const userVals = [...userTotal.values()].sort((a, b) => b - a);
  const decile = Math.max(1, Math.ceil(0.1 * userVals.length));
  const topDecileUserShare = Math.round((100 * userVals.slice(0, decile).reduce((a, b) => a + b, 0)) / totalAll);
  let topAccount = null, topMax = -1;
  for (const [c, v] of compTotal) if (v > topMax) { topMax = v; topAccount = c; }
  return { weeks, top1, hhi, topDecileUserShare, topAccount, topAccountSharePct: Math.round((100 * topMax) / totalAll) };
}

/** Per-account health: users, threading, recency, dormancy. */
export function accountHealth(events, opts = {}) {
  const aliases = opts.aliases || DEFAULT_COMPANY_ALIASES;
  const dormantWeeks = opts.dormantWeeks ?? 4;
  if (!events.length) return [];
  const maxDay = events.map((e) => dayKey(e.ts)).reduce((a, b) => (a > b ? a : b));
  const maxT = Date.parse(maxDay);
  const acc = new Map();
  for (const e of events) {
    const c = companyOf(e.user, aliases);
    if (!acc.has(c)) acc.set(c, { company: c, users: new Set(), interactions: 0, first: e.ts, last: e.ts });
    const a = acc.get(c);
    a.users.add(e.user); a.interactions += 1;
    if (e.ts < a.first) a.first = e.ts;
    if (e.ts > a.last) a.last = e.ts;
  }
  return [...acc.values()].map((a) => {
    const weeksSince = Math.floor((maxT - Date.parse(dayKey(a.last))) / (7 * 86400000));
    return {
      company: a.company,
      users: a.users.size,
      threading: a.users.size >= 2 ? 'multi' : 'single',
      interactions: a.interactions,
      firstActive: dayKey(a.first),
      lastActive: dayKey(a.last),
      weeksSinceActive: weeksSince,
      dormant: weeksSince >= dormantWeeks,
    };
  }).sort((x, y) => y.interactions - x.interactions);
}

/** Quality / ops from per-event status + latency (where logged). */
export function opsMetrics(events) {
  const lat = events.map((e) => e.latencyMs).filter((v) => typeof v === 'number' && !isNaN(v));
  const withStatus = events.filter((e) => typeof e.status === 'number');
  const errors = withStatus.filter((e) => e.status >= 400).length;
  return {
    latencyCount: lat.length,
    p50Ms: lat.length ? +percentile(lat, 50).toFixed(2) : null,
    p95Ms: lat.length ? +percentile(lat, 95).toFixed(2) : null,
    statusCount: withStatus.length,
    errorRatePct: withStatus.length ? +((100 * errors) / withStatus.length).toFixed(2) : null,
  };
}

// ===========================================================================
// Trends & distribution visualizations (all computable from the snapshot)
// ===========================================================================

/** Order label keys of a counter by descending total, capped to topN + "Other". */
function topLabels(totals, topN, otherLabel = 'Other') {
  const ordered = Object.entries(totals).sort((a, b) => b[1] - a[1]).map(([k]) => k);
  if (ordered.length <= topN) return { labels: ordered, other: [] };
  return { labels: ordered.slice(0, topN), other: ordered.slice(topN) };
}

/**
 * Weekly volume by category (scientific area or use case) for a stacked-area
 * trend. Multi-label: an interaction counts toward each of its labels.
 * dim = 'area' | 'use'. Unclassified prompts fall into "Unclassified".
 */
export function topicTrend(events, classMap, opts = {}) {
  const dim = opts.dim === 'use' ? 'uses' : 'areas';
  const topN = opts.topN ?? 7;
  if (!events.length) return { weeks: [], series: [] };
  const { weeks, wIndex } = weekIndexer(events);
  const totals = {};
  const perLabel = new Map(); // label -> data[]
  const add = (label, i) => {
    if (!perLabel.has(label)) perLabel.set(label, new Array(weeks.length).fill(0));
    perLabel.get(label)[i] += 1;
    totals[label] = (totals[label] || 0) + 1;
  };
  for (const e of events) {
    const i = wIndex.get(isoWeekStart(e.ts));
    if (i == null) continue;
    const labels = (classMap[e.text] && classMap[e.text][dim] && classMap[e.text][dim].length)
      ? classMap[e.text][dim] : ['Unclassified'];
    for (const l of labels) add(l, i);
  }
  const { labels, other } = topLabels(totals, topN);
  const series = labels.map((l) => ({ label: l, data: perLabel.get(l) }));
  if (other.length) {
    const data = new Array(weeks.length).fill(0);
    for (const l of other) perLabel.get(l).forEach((v, i) => { data[i] += v; });
    series.push({ label: `Other (${other.length})`, data });
  }
  return { weeks, series };
}

/** Per-user interaction totals, Gini coefficient, and a Lorenz curve. */
export function usageDistribution(events) {
  if (!events.length) return { counts: [], gini: 0, lorenz: [{ x: 0, y: 0 }], users: 0, interactions: 0, top1Pct: 0, top10Pct: 0 };
  const byUser = {};
  for (const e of events) byUser[e.user] = (byUser[e.user] || 0) + 1;
  const desc = Object.values(byUser).sort((a, b) => b - a);
  const asc = [...desc].sort((a, b) => a - b);
  const n = asc.length;
  const total = asc.reduce((a, b) => a + b, 0);
  // Gini
  let cumWeighted = 0;
  asc.forEach((x, i) => { cumWeighted += (i + 1) * x; });
  const gini = total > 0 ? +(((2 * cumWeighted) / (n * total)) - (n + 1) / n).toFixed(3) : 0;
  // Lorenz (ascending)
  const lorenz = [{ x: 0, y: 0 }];
  let cum = 0;
  asc.forEach((x, i) => { cum += x; lorenz.push({ x: Math.round((100 * (i + 1)) / n), y: Math.round((100 * cum) / total) }); });
  const topK = (k) => Math.round((100 * desc.slice(0, Math.max(1, Math.ceil(k * n))).reduce((a, b) => a + b, 0)) / total);
  return { counts: desc, gini, lorenz, users: n, interactions: total, top1Pct: topK(0.01), top10Pct: topK(0.1) };
}

/** Per-account seat growth (cumulative distinct users) + active-user momentum. */
export function accountExpansion(events, opts = {}) {
  const aliases = opts.aliases || DEFAULT_COMPANY_ALIASES;
  if (!events.length) return { weeks: [], companies: [] };
  const { weeks, wIndex } = weekIndexer(events);
  const N = weeks.length;
  const firstSeen = new Map();   // `${company}${user}` -> first week idx
  const activeCW = new Map();    // company -> Set per week
  const compOf = {};
  for (const e of events) {
    const i = wIndex.get(isoWeekStart(e.ts));
    if (i == null) continue;
    const c = companyOf(e.user, aliases);
    compOf[e.user] = c;
    const k = c + SEP + e.user;
    if (!firstSeen.has(k) || i < firstSeen.get(k)) firstSeen.set(k, i);
    if (!activeCW.has(c)) activeCW.set(c, weeks.map(() => new Set()));
    activeCW.get(c)[i].add(e.user);
  }
  const companies = [...activeCW.keys()].map((c) => {
    const activeWeekly = activeCW.get(c).map((s) => s.size);
    const seatsCum = new Array(N).fill(0);
    for (const [k, fi] of firstSeen) { if (k.startsWith(c + '')) for (let i = fi; i < N; i++) seatsCum[i] += 1; }
    const recent = activeWeekly.slice(Math.max(0, N - 4)).reduce((a, b) => a + b, 0);
    const prior = activeWeekly.slice(Math.max(0, N - 8), Math.max(0, N - 4)).reduce((a, b) => a + b, 0);
    const trend = recent > prior ? 'expanding' : recent < prior ? 'contracting' : 'flat';
    return { company: c, seats: seatsCum[N - 1], seatsCum, activeWeekly, recent, prior, trend };
  }).sort((a, b) => b.seats - a.seats);
  return { weeks, companies };
}

/** Day-of-week (Mon=0) × hour-of-day (UTC) activity matrix. */
export function activityCalendar(events) {
  const matrix = Array.from({ length: 7 }, () => new Array(24).fill(0));
  let max = 0;
  for (const e of events) {
    const d = new Date(e.ts);
    const dow = (d.getUTCDay() + 6) % 7;
    const hr = d.getUTCHours();
    matrix[dow][hr] += 1;
    if (matrix[dow][hr] > max) max = matrix[dow][hr];
  }
  const byDow = matrix.map((r) => r.reduce((a, b) => a + b, 0));
  const byHour = new Array(24).fill(0);
  for (let d = 0; d < 7; d++) for (let h = 0; h < 24; h++) byHour[h] += matrix[d][h];
  return { matrix, max, byDow, byHour };
}

/** Activation/retention funnel: tried → returned → champion → sustained. */
export function adoptionFunnel(events, opts = {}) {
  const championMin = opts.championMin ?? CHAMPION_MIN_QUERIES;
  const sustainedWeeks = opts.sustainedWeeks ?? 4;
  if (!events.length) return { stages: [] };
  const { weeks, wIndex } = weekIndexer(events);
  const { active } = userWeekSets(events, wIndex);
  const totalByUser = {};
  for (const e of events) totalByUser[e.user] = (totalByUser[e.user] || 0) + 1;
  const users = [...active.keys()];
  const tried = users.length;
  const returned = users.filter((u) => active.get(u).size >= 2).length;
  const champion = users.filter((u) => (totalByUser[u] || 0) >= championMin).length;
  const sustained = users.filter((u) => active.get(u).size >= sustainedWeeks).length;
  const pct = (x) => (tried ? Math.round((100 * x) / tried) : 0);
  return {
    stages: [
      { stage: 'Tried (≥1 query)', count: tried, pct: 100 },
      { stage: 'Returned (≥2 weeks)', count: returned, pct: pct(returned) },
      { stage: `Champion (≥${championMin} queries)`, count: champion, pct: pct(champion) },
      { stage: `Sustained (≥${sustainedWeeks} active weeks)`, count: sustained, pct: pct(sustained) },
    ],
  };
}

/** Topic breadth per account: distinct areas & use cases each company touches. */
export function engagementBreadth(events, classMap, opts = {}) {
  const aliases = opts.aliases || DEFAULT_COMPANY_ALIASES;
  if (!events.length) return { byAccount: [] };
  const acc = new Map();
  for (const e of events) {
    const c = companyOf(e.user, aliases);
    if (!acc.has(c)) acc.set(c, { company: c, areas: new Set(), uses: new Set(), interactions: 0 });
    const a = acc.get(c);
    a.interactions += 1;
    const cm = classMap[e.text];
    if (cm) { (cm.areas || []).forEach((x) => a.areas.add(x)); (cm.uses || []).forEach((x) => a.uses.add(x)); }
  }
  const byAccount = [...acc.values()]
    .map((a) => ({ company: a.company, areas: a.areas.size, uses: a.uses.size, interactions: a.interactions }))
    .sort((x, y) => y.interactions - x.interactions);
  return { byAccount };
}

/** Weekly interaction volume split into from-new-users vs from-returning-users. */
export function newVsReturningVolume(events) {
  if (!events.length) return { weeks: [], newVol: [], returningVol: [] };
  const { weeks, wIndex } = weekIndexer(events);
  const first = new Map();
  for (const e of events) {
    const i = wIndex.get(isoWeekStart(e.ts));
    if (i == null) continue;
    if (!first.has(e.user) || i < first.get(e.user)) first.set(e.user, i);
  }
  const newVol = new Array(weeks.length).fill(0);
  const returningVol = new Array(weeks.length).fill(0);
  for (const e of events) {
    const i = wIndex.get(isoWeekStart(e.ts));
    if (i == null) continue;
    if (first.get(e.user) === i) newVol[i] += 1; else returningVol[i] += 1;
  }
  return { weeks, newVol, returningVol };
}

/** Interaction counts by logging source (deployment/cell). */
export function sourceBreakdown(events) {
  const counts = {};
  for (const e of events) { const s = e.source || 'unknown'; counts[s] = (counts[s] || 0) + 1; }
  const total = events.length || 1;
  return Object.entries(counts)
    .map(([source, count]) => ({ source, count, pct: Math.round((100 * count) / total) }))
    .sort((a, b) => b.count - a.count);
}

/** Account momentum: last-4-weeks interactions vs prior-4-weeks, flag declines. */
export function churnRisk(events, opts = {}) {
  const aliases = opts.aliases || DEFAULT_COMPANY_ALIASES;
  if (!events.length) return [];
  const { weeks, wIndex } = weekIndexer(events);
  const N = weeks.length;
  const comp = new Map(); // company -> weekly interaction counts
  for (const e of events) {
    const i = wIndex.get(isoWeekStart(e.ts));
    if (i == null) continue;
    const c = companyOf(e.user, aliases);
    if (!comp.has(c)) comp.set(c, new Array(N).fill(0));
    comp.get(c)[i] += 1;
  }
  return [...comp.entries()].map(([company, arr]) => {
    const recent = arr.slice(Math.max(0, N - 4)).reduce((a, b) => a + b, 0);
    const prior = arr.slice(Math.max(0, N - 8), Math.max(0, N - 4)).reduce((a, b) => a + b, 0);
    const pctChange = prior > 0 ? Math.round((100 * (recent - prior)) / prior) : (recent > 0 ? null : 0);
    let lastActive = -1;
    for (let i = N - 1; i >= 0; i--) if (arr[i] > 0) { lastActive = i; break; }
    return { company, recent, prior, pctChange, weeksIdle: lastActive >= 0 ? N - 1 - lastActive : N, total: arr.reduce((a, b) => a + b, 0) };
  }).sort((a, b) => (a.pctChange ?? 999) - (b.pctChange ?? 999));
}

/**
 * Average retention curve by segment. segmenter(user) -> segment name. Default
 * segments accounts as "named" (>=25 interactions or >=2 users) vs "emerging".
 */
export function retentionBySegment(events, opts = {}) {
  const aliases = opts.aliases || DEFAULT_COMPANY_ALIASES;
  const maxK = opts.maxK ?? 8;
  if (!events.length) return { segments: [] };
  const { weeks, wIndex } = weekIndexer(events);
  const { active, first } = userWeekSets(events, wIndex);
  const N = weeks.length;

  // classify each user's company as named/emerging
  const compCount = {}, compUsers = {};
  for (const e of events) {
    const c = companyOf(e.user, aliases);
    compCount[c] = (compCount[c] || 0) + 1;
    (compUsers[c] || (compUsers[c] = new Set())).add(e.user);
  }
  const named = new Set(Object.keys(compCount).filter((c) => compCount[c] >= NAMED_MIN_INTERACTIONS || compUsers[c].size >= 2));
  const segOf = opts.segmenter || ((u) => (named.has(companyOf(u, aliases)) ? 'Named accounts' : 'Emerging accounts'));

  const buckets = new Map(); // seg -> { users:[] }
  for (const u of active.keys()) {
    const s = segOf(u);
    if (!buckets.has(s)) buckets.set(s, []);
    buckets.get(s).push(u);
  }
  const segments = [...buckets.entries()].map(([name, us]) => {
    const K = Math.min(maxK, N);
    const curve = [];
    for (let k = 0; k < K; k++) {
      let elig = 0, act = 0;
      for (const u of us) {
        const f = first.get(u);
        if (f + k < N) { elig += 1; if (active.get(u).has(f + k)) act += 1; }
      }
      curve.push(elig ? Math.round((100 * act) / elig) : null);
    }
    return { name, size: us.length, curve };
  }).sort((a, b) => b.size - a.size);
  return { segments };
}
